import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { personName } from '@/i18n/personName';
import { accountsApi, type Permission, type PermissionFlags } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { sharesApi } from '@/api/shares';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { useConfirm } from '@/components/useConfirm';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { useMe } from '@/session/meHooks';
import { allocationErrorKey } from './allocationErrors';
import { useUnsavedGuard } from '@/shared/useUnsavedGuard';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { useErrorFocus } from '@/shared/useErrorFocus';
import { useDirty } from '@/shared/useDirty';

interface PermissionsTabProps {
  accountId: number;
  readOnly: boolean;
  onNotify: (message: string) => void;
  onSubscriptionRequired: () => void;
}

interface Candidate {
  userId: number;
  /** Display name, then the served email; `null` renders "A member" (0097 §E 34). */
  displayName: string | null;
}

/**
 * Owner permissions tab (TMMAPP-0008, MM-D-11). Candidates are the distinct ACTIVE sharers
 * across the owner's `mine` virtual accounts (`sharesApi.list` per virtual account, de-duplicated by
 * `userId`) — the only people directory the SPA has.
 */
export function PermissionsTab({
  accountId,
  readOnly,
  onNotify,
  onSubscriptionRequired,
}: PermissionsTabProps) {
  const { t } = useTranslation();
  const me = useMe();
  const [rows, setRows] = useState<Permission[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [picked, setPicked] = useState<number | ''>('');
  const [flags, setFlags] = useState<PermissionFlags>({
    canImport: false,
    canViewFull: false,
    canEdit: false,
  });
  const [subscribe, setSubscribe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoking = useConfirm<Permission>();
  // The grant form is the unsaved work: a person picked, or a permission switched on. The tab is
  // a `?tab=` of the account page, so a tab change leaves — and is held — too (TMM-B5).
  const { dirty } = useDirty({ picked, flags });
  const guard = useUnsavedGuard(dirty, { includeSearch: true });
  const { rootRef, focusErrors } = useErrorFocus<HTMLDivElement>();
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      setRows(await accountsApi.permissions(accountId));
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [accountId]);

  // The fetch-once effect below survives StrictMode's re-mount on its ref guard, so its result is
  // gated on this mounted ref, not on a per-run flag (a cancelled first run would drop it).
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
    // Distinct ACTIVE sharers across the owner's mine virtual accounts — the only people directory
    // the SPA has; do not invent another source.
    const mine = me.virtualAccounts.filter((d) => d.mine);
    void Promise.all(mine.map((d) => sharesApi.list(d.id)))
      .then((lists) => {
        if (!mounted.current) return;
        const seen = new Map<number, Candidate>();
        for (const list of lists) {
          for (const share of list) {
            if (share.status !== 'ACTIVE' || share.userId === null) continue;
            if (!seen.has(share.userId)) {
              seen.set(share.userId, {
                userId: share.userId,
                displayName: share.displayName ?? share.email,
              });
            }
          }
        }
        setCandidates([...seen.values()]);
      })
      .catch(() => {
        if (mounted.current) setCandidates([]);
      });
  }, [load, me.virtualAccounts]);

  async function handleFlags(userId: number, next: PermissionFlags) {
    const applied: PermissionFlags = next.canEdit ? { ...next, canViewFull: true } : next;
    try {
      const saved = await accountsApi.putPermission(accountId, userId, applied);
      setRows((prev) =>
        prev === null ? prev : prev.map((row) => (row.userId === userId ? saved : row)),
      );
    } catch (err) {
      handleWriteError(err);
    }
  }

  async function handleRevoke() {
    await revoking.run(
      async ({ userId }) => {
        await accountsApi.revokePermission(accountId, userId);
        setRows((prev) => (prev === null ? prev : prev.filter((row) => row.userId !== userId)));
      },
      (err) => {
        if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
          setSubscribe(true);
          onSubscriptionRequired();
          return null;
        }
        return t(allocationErrorKey(err) ?? 'accounts.errorGeneric');
      },
    );
  }

  async function handleGrant() {
    if (typeof picked !== 'number') return;
    const applied: PermissionFlags = flags.canEdit ? { ...flags, canViewFull: true } : flags;
    try {
      const saved = await accountsApi.putPermission(accountId, picked, applied);
      setRows((prev) => [...(prev ?? []), saved]);
      setPicked('');
      setFlags({ canImport: false, canViewFull: false, canEdit: false });
      setError(null);
    } catch (err) {
      handleWriteError(err);
    }
  }

  function handleWriteError(err: unknown) {
    if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
      setSubscribe(true);
      onSubscriptionRequired();
    } else {
      const key = allocationErrorKey(err);
      setError(t(key ?? 'accounts.errorGeneric'));
      onNotify(t(key ?? 'accounts.errorGeneric'));
      focusErrors();
    }
  }

  const grantedIds = new Set((rows ?? []).map((r) => r.userId));
  const available = candidates.filter((c) => !grantedIds.has(c.userId));

  if (subscribe) return <SubscribeInPlace />;

  if (loadFailure !== null) {
    return (
      <ErrorState
        error={loadFailure.error}
        what={t('errors.what.permissions')}
        onRetry={() => void load()}
      />
    );
  }

  if (rows === null) return null;

  return (
    <Box ref={rootRef}>
      <UnsavedChangesDialog guard={guard} />
      {error !== null && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Table size="small" aria-label={t('accounts.tabPermissions')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('accounts.person')}</TableCell>
            <TableCell>{t('accounts.permImport')}</TableCell>
            <TableCell>{t('accounts.permViewFull')}</TableCell>
            <TableCell>{t('accounts.permEdit')}</TableCell>
            {!readOnly && <ActionsHeaderCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.userId}>
              <TableCell>{personName(row.displayName, t)}</TableCell>
              <TableCell>
                <Switch
                  checked={row.canImport}
                  disabled={readOnly}
                  onChange={(_event, checked) =>
                    void handleFlags(row.userId, { ...row, canImport: checked })
                  }
                  slotProps={{ input: { 'aria-label': t('accounts.permImport') } }}
                />
              </TableCell>
              <TableCell>
                <Switch
                  checked={row.canViewFull || row.canEdit}
                  disabled={readOnly || row.canEdit}
                  onChange={(_event, checked) =>
                    void handleFlags(row.userId, { ...row, canViewFull: checked })
                  }
                  slotProps={{ input: { 'aria-label': t('accounts.permViewFull') } }}
                />
              </TableCell>
              <TableCell>
                <Switch
                  checked={row.canEdit}
                  disabled={readOnly}
                  onChange={(_event, checked) =>
                    void handleFlags(row.userId, {
                      ...row,
                      canEdit: checked,
                      canViewFull: checked ? true : row.canViewFull,
                    })
                  }
                  slotProps={{ input: { 'aria-label': t('accounts.permEdit') } }}
                />
              </TableCell>
              {!readOnly && (
                <TableCell>
                  <Button size="small" color="error" onClick={() => revoking.ask(row)}>
                    {t('accounts.revoke')}
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {revoking.target !== null && (
        <ConfirmDialog
          title={t('accounts.revokeTitle', {
            name: personName(revoking.target.displayName, t),
          })}
          consequence={t('accounts.revokeConsequence')}
          confirmLabel={t('accounts.revokeConfirm')}
          destructive
          busy={revoking.busy}
          error={revoking.error}
          onConfirm={() => void handleRevoke()}
          onCancel={revoking.cancel}
        />
      )}

      {!readOnly && (
        <Stack spacing={1.5} sx={{ mt: 3, maxWidth: 480 }}>
          <Typography variant="subtitle1" component="h2">
            {t('accounts.addPermission')}
          </Typography>
          {available.length === 0 ? (
            <Typography variant="body2">{t('accounts.noCandidates')}</Typography>
          ) : (
            <>
              <FormControl fullWidth size="small">
                <InputLabel id="perm-candidate">{t('accounts.person')}</InputLabel>
                <Select
                  labelId="perm-candidate"
                  label={t('accounts.person')}
                  value={picked === '' ? '' : String(picked)}
                  onChange={(event) => {
                    const value = event.target.value;
                    setPicked(value === '' ? '' : Number(value));
                  }}
                >
                  {available.map((c) => (
                    <MenuItem key={c.userId} value={String(c.userId)}>
                      {personName(c.displayName, t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={flags.canImport}
                    onChange={(_event, checked) => setFlags((p) => ({ ...p, canImport: checked }))}
                  />
                }
                label={t('accounts.permImport')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={flags.canViewFull || flags.canEdit}
                    disabled={flags.canEdit}
                    onChange={(_event, checked) =>
                      setFlags((p) => ({ ...p, canViewFull: checked }))
                    }
                  />
                }
                label={t('accounts.permViewFull')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={flags.canEdit}
                    onChange={(_event, checked) =>
                      setFlags((p) => ({
                        ...p,
                        canEdit: checked,
                        canViewFull: checked ? true : p.canViewFull,
                      }))
                    }
                  />
                }
                label={t('accounts.permEdit')}
              />
              <Box>
                <Button
                  variant="contained"
                  onClick={() => void handleGrant()}
                  disabled={picked === ''}
                >
                  {t('accounts.grant')}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
